import { useState } from "react";
import { Loader2Icon, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Comment } from "@/types/comment";
import { useAuth } from "@/context/AuthContext";
import { CreateMetaData } from "@/components/shared/CreateMetaData";
import { TabsContent } from "@/components/ui/tabs";
import { COMMENT_TAB_ID } from "../MeasurementRequestDetails.values";

type CommentsTabProps = {
  comments: Comment[];
  allowCreate?: boolean;
  showComments?: boolean;
  onCreateComment: (comment: string) => Promise<void>;
  onDeleteComment: (commentId: number) => Promise<void>;
  isCreatingComment?: boolean;
};

export const CommentsTab = ({
  comments,
  allowCreate = true,
  showComments = true,
  onCreateComment,
  onDeleteComment,
  isCreatingComment = false,
}: CommentsTabProps) => {
  const { account } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateComment = async () => {
    if (!newComment.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    try {
      await onCreateComment(newComment);
      toast.success("Comment added successfully!");
      setNewComment("");
      setIsCreating(false);
    } catch (err) {
      toast.error(`Error: ${err instanceof Error ? err.message : "Failed to create comment"}`);
      console.error(err);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await onDeleteComment(commentId);
      toast.success("Comment deleted successfully!");
    } catch (err) {
      toast.error(`Error: ${err instanceof Error ? err.message : "Failed to delete comment"}`);
      console.error(err);
    }
  };

  return (
    <TabsContent
      key={COMMENT_TAB_ID}
      value={COMMENT_TAB_ID}
      className="flex-1 overflow-y-scroll max-h-[45vh]"
    >
      {/* Add New Comment */}
      {allowCreate && (
        <div className="space-y-3 pt-2">
          {isCreating ? (
            <div className="space-y-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write your comment here..."
                rows={3}
                className="resize-none"
                disabled={isCreatingComment}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setNewComment("");
                  }}
                  disabled={isCreatingComment}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreateComment}
                  disabled={isCreatingComment || !newComment.trim()}
                  type="button"
                >
                  {isCreatingComment && <Loader2Icon className="animate-spin mr-2 h-4 w-4" />}
                  Add Comment
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCreating(true)}
              className="text-blue-600 hover:text-blue-800"
              type="button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Comment
            </Button>
          )}
        </div>
      )}
      {/* Existing Comments */}
      {showComments && comments.length !== 0 && (
        <div className="space-y-3">
          {comments
            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
            .map((comment) => (
              <div
                key={comment.id}
                className="border border-gray-200 rounded-lg p-4 space-y-2 hover:border-gray-300 transition-colors"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {comment.comment}
                    </p>
                  </div>
                  {comment.createdBy === account.name ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                      type="button"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  ) : null}
                </div>
                <CreateMetaData
                  createdAt={comment.createdAt}
                  createdBy={comment.createdBy}
                  withTimestamp
                />
              </div>
            ))}
        </div>
      )}
    </TabsContent>
  );
};
